import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Router, useNavigate } from "react-router-dom";
import IosSwitchMain from "../../../components/iosSwitchMain";
import CloseIcon from "@mui/icons-material/Close";
import {
  LocalizationProvider,
  DatePicker,
  DateTimePicker,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs, { Dayjs } from "dayjs";
import { showError, showToast } from "../../../constants/toasts";
import { isNumber, isValidNumber } from "../../../utils/validations";
import {
  getFromStorage,
  removeFromStorage,
  setToStorage,
  STORAGE_KEYS,
} from "../../../helpers";
import { Offer, OfferData } from "../../../types/General";
import { OFFER_STATUS, QUESTION_TYPE } from "../../../utils/enums";
import { useLazyGetPreviousQuestionQuery } from "../../../service/user";
import {
  useCreateOfferMutation,
  useLazyCloneOfferQuery,
  useUpdateOfferMutation,
} from "../../../service/offer";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const EVENT_TYPE = {
  PAID: "Paid",
  COMPLIMENTARY: "Complimentary",
};

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

interface OthersFormProps {
  alignment: string;
  offerDetails: Offer | null;
  updateId: string | undefined;
  isClone: boolean;
  tabs: number;
  setTabs: Dispatch<SetStateAction<number>>;
  categoryTab: any;
  status: any;
  categoryName: string;
  categoryId: string;
}

interface QuestionOption {
  optionText: string;
}

interface QuestionData {
  question: string;
  inputType: string; // e.g., "text", "checkbox"
  isReused: boolean;
  options: QuestionOption[];
  label?: string;
}

function EventForm({
  alignment,
  offerDetails,
  updateId,
  isClone,
  tabs,
  setTabs,
  categoryId,
  categoryName,
  categoryTab,
  status,
}: OthersFormProps) {
  const navigate = useNavigate();
  const [getQuestions] = useLazyGetPreviousQuestionQuery();
  const [createOffer] = useCreateOfferMutation();
  const [updateOffer] = useUpdateOfferMutation();
  const [cloneOffer] = useLazyCloneOfferQuery();

  const offerData = getFromStorage(STORAGE_KEYS.offerData);
  const parsedData: OfferData | null = offerData
    ? (JSON.parse(offerData) as OfferData)
    : null;

  const [isCancellationEnabled, setIsCancellationEnabled] = useState(false);
  const [isGuestEnabled, setIsGuestEnabled] = useState(false);
  const [valueTabs, setValueTabs] = useState(0);
  const [date, setDate] = useState<Dayjs | null>(null);
  const [time, setTime] = useState<Dayjs | null>(null);
  const [selectedType, setSelectedType] = useState("Paid");
  const [questionTabs, setQuestionTabs] = useState(0);
  const [textQuestions, setTextQuestions] = useState<QuestionData[]>([]);
  const [checkboxQuestions, setCheckboxQuestions] = useState<QuestionData[]>(
    []
  );
  const [radioQuestions, setRadioQuestions] = useState<QuestionData[]>([]);
  const [dateQuestions, setDateQuestions] = useState<QuestionData[]>([]);
  const [textAreaQuestions, setTextAreaQuestions] = useState<QuestionData[]>(
    []
  );
  const [selectedQuestions, setSelectedQuestions] = useState<any[]>([]);
  const [openPrevQModal, setOpenPrevQModal] = useState(false);

  const [value, setValue] = useState<QuestionData>({
    inputType: "",
    question: "",
    options: [],
    isReused: false,
  });

  const [currentOption, setCurrentOption] = useState<string>("");
  const [questionsList, setQuestionsList] = useState<QuestionData[]>([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChangeType = (e: SelectChangeEvent<string>) => {
    setValue({
      ...value,
      inputType: e.target.value,
      options: [], // reset options when type changes
    });
  };

  const handleAddOption = () => {
    if (currentOption.trim()) {
      setValue((prev) => ({
        ...prev,
        options: [...prev.options, { optionText: currentOption.trim() }],
      }));
      setCurrentOption("");
    }
  };

  const handleSave = () => {
    if (!value.question.trim() || !value?.inputType) {
      showError("Please enter a question");
      return;
    }
    const formattedQuestion: QuestionData = {
      question: value.question.trim(),
      inputType:
        value.inputType === "Input" ? "text" : value.inputType.toLowerCase(),
      isReused: value.isReused,
      options: value.options, // already [{ optionText: string }]
    };

    setQuestionsList((prev) => [...prev, formattedQuestion]);

    setValue({
      question: "",
      inputType: "",
      isReused: false,
      options: [],
    });
    setCurrentOption("");
  };

  const handleEditQuestion = (idx: number) => {
    const q = questionsList[idx];
    setValue({
      question: q?.question || q?.label || "",
      inputType:
        q?.inputType === "text"
          ? "Input"
          : q?.inputType?.charAt(0)?.toUpperCase() + q?.inputType?.slice(1),
      isReused: q?.isReused,
      options: q?.options || [],
    });
    setQuestionsList((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleDeleteQuestion = (idx: number) => {
    setQuestionsList((prev) => prev?.filter((_, i) => i !== idx));
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === EVENT_TYPE.COMPLIMENTARY) {
      setIsCancellationEnabled(false);
      formik.setFieldValue("cancellationHours", "");
      formik.setFieldValue("cancellationAmount", "");
      formik.setFieldValue("nonMemberPrice", "0");
      formik.setFieldValue("memberPrice", "0");
    }
    if (event.target.value === EVENT_TYPE.PAID) {
      formik.setFieldValue("nonMemberPrice", "");
      formik.setFieldValue("memberPrice", "");
    }
    setSelectedType(event.target.value);
  };

  const handleSaveQuestion = () => {
    if (!selectedQuestions?.length) {
      showError("Please select questions");
      return;
    }

    const merged = [...questionsList, ...selectedQuestions];

    // remove duplicates by question text
    const unique = merged?.filter(
      (q, index, self) =>
        index === self?.findIndex((x) => x?.question === q?.question)
    );
    setValueTabs(0);
    setQuestionsList(unique);
    setOpenPrevQModal(false);
  };

  const handleCheckboxChange = (q: any, checked: boolean) => {
    const questionData: QuestionData = {
      question: q?.label,
      inputType: q?.inputType,
      isReused: true, // or false depending on your requirement
      options: q?.options || [],
    };

    if (checked) {
      setSelectedQuestions((prev) => [...prev, questionData]);
    } else {
      setSelectedQuestions((prev) =>
        prev.filter((item) => item.question !== q?.label)
      );
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      guests: parsedData?.NumberOfGuest ?? offerDetails?.numberOfGuest ?? "",
      nonMemberPrice:
        parsedData?.NoMemberPrice ?? offerDetails?.noMemberPrice ?? "",
      capacity: parsedData?.MaxCapacity ?? offerDetails?.maxCapacity ?? "",
      threshold:
        parsedData?.ThresholdCapacity ?? offerDetails?.thresholdCapacity ?? "",
      memberPrice: parsedData?.MemberPrice ?? offerDetails?.memberPrice ?? "",
      cancellationAmount:
        parsedData?.CancellationAmountPerPerson ??
        offerDetails?.cancellationAmountPerPerson ??
        "",
      cancellationHours:
        parsedData?.CancellationHours ?? offerDetails?.cancellationHours ?? "",
    },
    validationSchema: Yup.object({
      guests: isGuestEnabled
        ? Yup.string().required().required("Guests is required")
        : Yup.string().notRequired(),
      nonMemberPrice:
        isGuestEnabled && selectedType === EVENT_TYPE.PAID
          ? Yup.string().required().required("Non member price is required")
          : Yup.string().notRequired(),
      memberPrice:
        selectedType === EVENT_TYPE.PAID
          ? Yup.string().required().required("Member price is required")
          : Yup.string().notRequired(),
      capacity: Yup.string()
        .required()
        .required("Maximum capacity is required"),
      threshold: Yup.string()
        .required()
        .required("Threshold is required")
        .test(
          "is-less-than-capacity",
          "Threshold must be less than maximum capacity",
          function (value) {
            const { capacity } = this.parent;
            const thresholdNum = Number(value);
            const capacityNum = Number(capacity);

            if (isNaN(thresholdNum) || isNaN(capacityNum)) return false; // invalid number
            return thresholdNum < capacityNum;
          }
        ),
      cancellationAmount: isCancellationEnabled
        ? Yup.string().required().required("Cancellation amount is required")
        : Yup.string().notRequired(),
      cancellationHours: isCancellationEnabled
        ? Yup.string().required().required("Cancellation hours is required")
        : Yup.string().notRequired(),
    }),

    onSubmit: async (values) => {
      formik.setSubmitting(true);

      if (!date) {
        return;
      }

      if (!updateId && dayjs(date).isBefore(dayjs(), "day")) {
        return;
      }

      if (!time) {
        return;
      }

      const newBody = {
        IsGuestAdded: isGuestEnabled,
        ...(isGuestEnabled ? { NumberOfGuest: values.guests || "" } : {}),
        ...(isGuestEnabled && selectedType === EVENT_TYPE.PAID
          ? { NoMemberPrice: values.nonMemberPrice || "" }
          : {}),
        EventDateTime: date ? dayjs(date).toISOString() : "",
        EventTime: time ? dayjs(time).toISOString() : "",
        IsCancellationPolicy: isCancellationEnabled,
        QuestionJson: questionsList,
        ...(isCancellationEnabled
          ? { CancellationAmountPerPerson: values.cancellationAmount || "" }
          : {}),
        ...(isCancellationEnabled
          ? { CancellationHours: values.cancellationHours || "" }
          : {}),
        MaxCapacity: values.capacity,
        ThresholdCapacity: values.threshold,
        EventType: selectedType,
        ...(selectedType === EVENT_TYPE.PAID
          ? { MemberPrice: values.memberPrice }
          : {}),
        IsActive: alignment === OFFER_STATUS.ACTIVE ? true : false,
        IsArchive: alignment === OFFER_STATUS.ARCHIVE ? true : false,
      };

      const prevData = getFromStorage(STORAGE_KEYS.offerData);
      let parsedPrevData = {};

      if (prevData) {
        try {
          parsedPrevData = JSON.parse(prevData);
        } catch (err) {
          console.error("Error parsing stored offer data:", err);
        }
      }

      // Merge old and new data
      const mergedData = {
        ...parsedPrevData,
        ...newBody,
      };

      setToStorage(STORAGE_KEYS.offerData, JSON.stringify(mergedData));

      navigate("/manage-offer/preview-offer?mode=EVENTS", {
        state: {
          updateId,
          isClone,
          status,
          categoryTab,
          categoryId,
          categoryName,
        },
      });
    },
  });

  const handleArchive = async () => {
    const formData = new FormData();

    const appendIfPresent = (key: string, value: any) => {
      if (value !== undefined && value !== null && value !== "") {
        formData.append(key, value);
      }
    };

    const appendArray = (key: string, arr?: any[]) => {
      if (Array.isArray(arr)) {
        arr.forEach((item) => {
          if (item !== undefined && item !== null && item !== "") {
            formData.append(key, item);
          }
        });
      }
    };

    appendIfPresent("Title", parsedData?.Title);
    appendIfPresent("Badge", parsedData?.Badge);
    appendIfPresent("Description", parsedData?.Description);
    appendIfPresent("Subtitle", parsedData?.Subtitle);
    appendIfPresent("CategoryId", parsedData?.CategoryId);
    appendIfPresent("OfferType", parsedData?.OfferType);

    appendIfPresent(
      "OfferDisplayStartDate",
      parsedData?.OfferDisplayStartDate
        ? dayjs(parsedData?.OfferDisplayTime).toISOString()
        : null
    );

    appendIfPresent(
      "OfferDisplayEndDate",
      parsedData?.OfferDisplayEndDate
        ? dayjs(parsedData?.OfferDisplayEndTime).toISOString()
        : null
    );

    appendIfPresent(
      "IsButterflyEffect",
      parsedData?.IsButterflyEffect ? "true" : "false"
    );

    appendIfPresent(
      "ButterflyEffectStartDate",
      parsedData?.ButterflyEffectStartDate
        ? dayjs(parsedData?.ButterflyEffectStartTime).toISOString()
        : null
    );

    appendIfPresent(
      "ButterflyEffectEndDate", // fixed the duplicate key name
      parsedData?.ButterflyEffecteEndDate
        ? dayjs(parsedData?.ButterflyEffectEndTime).toISOString()
        : null
    );

    appendIfPresent("ButtonName", parsedData?.ButtonName);
    appendArray("GroupsId", parsedData?.GroupsId);
    appendArray("TagsId", parsedData?.TagsId);
    appendArray("offerImages", parsedData?.offerImages);

    appendIfPresent(
      "IsCancellationPolicy",
      isCancellationEnabled ? "true" : "false"
    );
    appendIfPresent(
      "CancellationAmountPerPerson",
      formik.values.cancellationAmount
    );
    appendIfPresent("CancellationHours", formik.values.cancellationHours);
    appendIfPresent("QuestionJson", JSON.stringify(questionsList || []));
    appendIfPresent(
      "OfferDisplayTime",
      parsedData?.OfferDisplayTime
        ? dayjs(parsedData?.OfferDisplayTime).format("HH:mm:ss")
        : null
    );

    appendIfPresent(
      "OfferDisplayEndTime",
      parsedData?.OfferDisplayEndTime
        ? dayjs(parsedData?.OfferDisplayEndTime).format("HH:mm:ss")
        : null
    );
    appendIfPresent(
      "ButterflyEffectStartTime",
      parsedData?.ButterflyEffectStartTime
        ? dayjs(parsedData?.ButterflyEffectStartTime).format("HH:mm:ss")
        : null
    );
    appendIfPresent(
      "ButterflyEffectEndTime",
      parsedData?.ButterflyEffectEndTime
        ? dayjs(parsedData?.ButterflyEffectEndTime).format("HH:mm:ss")
        : null
    );
    appendIfPresent("EventDateTime", date ? dayjs(time).toISOString() : null);
    appendIfPresent(
      "EventTime",
      parsedData?.EventTime
        ? dayjs(parsedData?.EventTime).format("HH:mm:ss")
        : null
    );

    appendIfPresent("IsGuestAdded", isGuestEnabled ? "true" : "false");
    appendIfPresent("NumberOfGuest", formik.values.guests);
    appendIfPresent("NoMemberPrice", formik.values.nonMemberPrice);
    appendIfPresent("MaxCapacity", formik.values.capacity);
    appendIfPresent("ThresholdCapacity", formik.values.threshold);
    appendIfPresent("EventType", selectedType);
    appendIfPresent("MemberPrice", formik.values.memberPrice);
    appendIfPresent(
      "IsActive",
      alignment === OFFER_STATUS.ACTIVE ? "true" : "false"
    );
    appendIfPresent(
      "IsArchive",
      alignment === OFFER_STATUS.ARCHIVE ? "true" : "false"
    );
    appendIfPresent("OfferId", updateId);

    if (!updateId || isClone) {
      try {
        setLoading(true);
        const response = await createOffer(formData).unwrap();
        setLoading(false);
        if (response?.status === 200) {
          removeFromStorage(STORAGE_KEYS.offerData);
          // navigate("/manage-offer/preview-offer-detail");
          showToast(response?.message || "");
          navigate("/manage-offer", {
            state: { status, categoryTab, categoryId, categoryName },
          });
        } else {
          showError(response?.message || "");
        }
      } catch (error: any) {
        setLoading(false);
        showError(error?.data?.message || "");
        console.error(error);
      }
    } else {
      try {
        setLoading(true);
        const response = await updateOffer(formData).unwrap();
        setLoading(false);
        if (response?.status === 200) {
          removeFromStorage(STORAGE_KEYS.offerData);
          // navigate("/manage-offer/preview-offer-detail");
          showToast(response?.message || "");
          navigate("/manage-offer", {
            state: { status, categoryTab, categoryId, categoryName },
          });
        } else {
          showError(response?.message || "");
        }
      } catch (error: any) {
        setLoading(false);
        showError(error?.data?.message || "");
        console.error(error);
      }
    }
  };

  const cloneOfferApi = async () => {
    try {
      const response = await cloneOffer({ offerId: updateId }).unwrap();
      if (response.status === 200) {
        removeFromStorage(STORAGE_KEYS.offerData);
        // navigate("/manage-offer/preview-offer-detail");
        showToast(response?.message || "");
        navigate("/manage-offer", {
          state: { status: 3, categoryTab, categoryId, categoryName },
        });
      } else {
        showError(response?.message || "");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const handleChangeTab = (e: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);

    if (newValue === 1) {
      setOpenPrevQModal(true);
    }
  };

  const getPreviousQuestions = async () => {
    try {
      const response = await getQuestions({
        page: 1,
        search: "",
      }).unwrap();
      if (response.status === 200) {
        setCheckboxQuestions(
          response?.data?.filter(
            (q: any) => q?.inputType === QUESTION_TYPE.CHECKBOX
          )
        );
        setTextQuestions(
          response?.data?.filter(
            (q: any) => q?.inputType === QUESTION_TYPE.TEXT
          )
        );
        setRadioQuestions(
          response?.data?.filter(
            (q: any) => q?.inputType === QUESTION_TYPE.RADIO
          )
        );
        setTextAreaQuestions(
          response?.data?.filter(
            (q: any) => q?.inputType === QUESTION_TYPE.TEXTAREA
          )
        );
        setDateQuestions(
          response?.data?.filter(
            (q: any) => q?.inputType === QUESTION_TYPE.DATE
          )
        );
      } else {
        setCheckboxQuestions([]);
        setTextQuestions([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getPreviousQuestions();
  }, []);

  useEffect(() => {
    setIsGuestEnabled(
      parsedData?.IsGuestAdded ?? offerDetails?.isGuestAdded ?? false
    );
    setIsCancellationEnabled(
      parsedData?.IsCancellationPolicy ??
        offerDetails?.isCancellationPolicy ??
        false
    );
    setQuestionsList(
      parsedData?.QuestionJson
        ? parsedData?.QuestionJson
        : offerDetails?.questions
          ? offerDetails?.questions?.map((q) => ({
              question: q?.label,
              inputType: q?.inputType?.toLowerCase(),
              isReused: q?.isReused,
              options: q?.options ?? [],
            }))
          : []
    );
    setSelectedType(parsedData?.EventType ?? offerDetails?.eventType ?? "Paid");
    setDate(
      parsedData?.EventDateTime
        ? dayjs(parsedData.EventDateTime)
        : offerDetails?.eventDateTime
          ? dayjs(offerDetails.eventDateTime)
          : null
    );
    setTime(
      parsedData?.EventTime
        ? dayjs(parsedData?.EventTime)
        : offerDetails?.eventTime
          ? dayjs(offerDetails?.eventDateTime)
          : null
    );
  }, []);

  return (
    <>
      <div className="section_title mn_hdng">
        <h2>Event</h2>

        <div className="btn_flex flx_end" style={{}}>
          <Button className="btn btn_primary" onClick={() => setTabs(0)}>
            Back
          </Button>
          {alignment === "archived" && (
            <Button
              className="btn btn_primary"
              onClick={() => {
                if (updateId) {
                  cloneOfferApi();
                } else {
                  handleArchive();
                }
              }}
            >
              Save & Exit
            </Button>
          )}
          <Button
            className="btn btn_primary"
            onClick={() => {
              formik.submitForm();
              setError(true);
            }}
            type="submit"
          >
            Preview
          </Button>
        </div>
      </div>

      <div className="gap_m avnc_bkng">
        <form>
          <div className="control_group w_100">
            <div className="gap_m">
              <div className="cncl_plcy control_group w_100">
                <label>Add Guest</label>
                <IosSwitchMain
                  checked={isGuestEnabled}
                  onChange={(e) => setIsGuestEnabled(e.target.checked)}
                />
              </div>
              {isGuestEnabled && (
                <>
                  <div className="control_group w_50">
                    <label htmlFor="guests">No. of Guests</label>
                    <TextField
                      placeholder="Enter Number"
                      hiddenLabel
                      fullWidth
                      value={formik.values.guests}
                      id="guests"
                      name="guests"
                      className="text_field"
                      inputProps={{ maxLength: 10 }}
                      onChange={(e) => {
                        const { value } = e.target;
                        if (isNumber(value)) {
                          formik.handleChange(e);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      helperText={
                        formik.touched.guests &&
                        typeof formik.errors.guests === "string"
                          ? formik.errors.guests
                          : ""
                      }
                    />
                  </div>
                  {selectedType === EVENT_TYPE.PAID ? (
                    <div className="control_group w_50">
                      <label htmlFor="nonMemberPrice">Non-Member Price</label>
                      <TextField
                        placeholder="Enter Price"
                        hiddenLabel
                        fullWidth
                        value={formik.values.nonMemberPrice}
                        id="nonMemberPrice"
                        name="nonMemberPrice"
                        className="text_field"
                        inputProps={{ maxLength: 10 }}
                        onChange={(e) => {
                          const { value } = e.target;
                          if (isNumber(value)) {
                            formik.handleChange(e);
                          }
                        }}
                        onBlur={formik.handleBlur}
                        helperText={
                          formik.touched.nonMemberPrice &&
                          typeof formik.errors.nonMemberPrice === "string"
                            ? formik.errors.nonMemberPrice
                            : ""
                        }
                      />
                    </div>
                  ) : (
                    <div className="control_group w_50" />
                  )}
                </>
              )}
              <div className="w_50 control_group date_field">
                <label>Date</label>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    className="form_control"
                    format="DD/MM/YYYY"
                    minDate={dayjs()}
                    value={date}
                    slotProps={{
                      textField: {
                        placeholder: "Start Date",
                        sx: {
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "#9999", // change text & placeholder color
                            opacity: 1,
                          },
                        },
                      },
                    }}
                    onChange={(newValue) => setDate(newValue)}
                    slots={{
                      openPickerIcon: () => (
                        <img
                          src="/static/images/calendar_icon.svg"
                          alt="icon"
                        />
                      ),
                      // textField: (params) => (
                      //   <TextField
                      //     {...params}
                      //     fullWidth
                      //     placeholder="Start Date"
                      //     hiddenLabel
                      //   />
                      // ),
                    }}
                    desktopModeMediaQuery="(min-width:0px)"
                  />
                </LocalizationProvider>

                {error && !date ? (
                  <p className="err_msg">Start Date is required</p>
                ) : !updateId &&
                  error &&
                  date &&
                  dayjs(date).isBefore(dayjs(), "day") ? (
                  <p className="err_msg">
                    Start Date cannot be earlier than today
                  </p>
                ) : (
                  ""
                )}
              </div>

              <div className="w_50 control_group date_field">
                <label> Time</label>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <TimePicker
                    value={time}
                    onChange={(newValue) => {
                      if (date) {
                        setTime(
                          dayjs(date)
                            .hour(dayjs(newValue).hour())
                            .minute(dayjs(newValue).minute())
                        );
                      }
                    }}
                    slotProps={{
                      textField: {
                        placeholder: "Start Time",
                        sx: {
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "#9999", // change text & placeholder color
                            opacity: 1,
                          },
                        },
                      },
                    }}
                    slots={{
                      openPickerIcon: () => (
                        <img
                          src="/static/images/calendar_icon.svg"
                          alt="icon"
                        />
                      ),
                    }}
                    disabled={!date}
                  />
                </LocalizationProvider>
                {error && !time ? (
                  <p className="err_msg">Time is required</p>
                ) : (
                  ""
                )}
              </div>

              <div className="control_group w_50">
                <label htmlFor="capacity">Maximum capacity number</label>
                <TextField
                  placeholder="Maximum capacity number"
                  hiddenLabel
                  fullWidth
                  value={formik.values.capacity}
                  id="capacity"
                  name="capacity"
                  className="text_field"
                  inputProps={{ maxLength: 10 }}
                  onChange={(e) => {
                    const { value } = e.target;
                    if (isValidNumber(value)) {
                      formik.handleChange(e);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.capacity &&
                    typeof formik.errors.capacity === "string"
                      ? formik.errors.capacity
                      : ""
                  }
                />
              </div>

              <div className="control_group w_50">
                <label htmlFor="threshold">Threshold (on minimum value)</label>
                <TextField
                  placeholder="Threshold"
                  hiddenLabel
                  fullWidth
                  value={formik.values.threshold}
                  id="threshold"
                  name="threshold"
                  className="text_field"
                  inputProps={{ maxLength: 10 }}
                  onChange={(e) => {
                    const { value } = e.target;
                    if (isNumber(value)) {
                      formik.handleChange(e);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.threshold &&
                    typeof formik.errors.threshold === "string"
                      ? formik.errors.threshold
                      : ""
                  }
                />
              </div>

              <div className="control_group w_100">
                <label>Type</label>

                <FormControl className="paid_radio">
                  <RadioGroup
                    aria-labelledby="demo-radio-buttons-group-label"
                    defaultValue="Paid"
                    name="radio-buttons-group"
                    value={selectedType}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="Paid"
                      control={<Radio />}
                      label="Paid"
                    />
                    <FormControlLabel
                      value="Complimentary"
                      control={<Radio />}
                      label="Complimentary"
                    />
                  </RadioGroup>
                </FormControl>
              </div>

              {selectedType === EVENT_TYPE.PAID ? (
                <div className="control_group w_50">
                  <label htmlFor="memberPrice">Member Price</label>
                  <TextField
                    placeholder="Enter Price"
                    hiddenLabel
                    fullWidth
                    value={formik.values.memberPrice}
                    id="memberPrice"
                    name="memberPrice"
                    className="text_field"
                    inputProps={{ maxLength: 10 }}
                    onChange={(e) => {
                      const { value } = e.target;
                      if (isNumber(value)) {
                        formik.handleChange(e);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    helperText={
                      formik.touched.memberPrice &&
                      typeof formik.errors.memberPrice === "string"
                        ? formik.errors.memberPrice
                        : ""
                    }
                  />
                </div>
              ) : (
                <div className="control_group w_50" />
              )}

              <div className=" control_group w_100">
                <div className="gap_m">
                  <div className="control_group cncl_plcy w_50">
                    <label>Cancellation Policy</label>
                    <IosSwitchMain
                      checked={isCancellationEnabled}
                      onChange={(e) =>
                        setIsCancellationEnabled(e.target.checked)
                      }
                      disabled={selectedType === EVENT_TYPE.COMPLIMENTARY}
                    />
                  </div>
                </div>
              </div>

              {isCancellationEnabled && (
                <>
                  <div className="control_group w_50">
                    <label htmlFor="cancellationAmount">
                      Cancellation Amount Per Person
                    </label>
                    <TextField
                      placeholder="Enter cancellation charges"
                      hiddenLabel
                      fullWidth
                      value={formik.values.cancellationAmount}
                      id="cancellationAmount"
                      name="cancellationAmount"
                      className="text_field"
                      inputProps={{ maxLength: 40 }}
                      onChange={(e) => {
                        const { value } = e.target;
                        if (isNumber(value)) {
                          formik.handleChange(e);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      helperText={
                        formik.touched.cancellationAmount &&
                        typeof formik.errors.cancellationAmount === "string"
                          ? formik.errors.cancellationAmount
                          : ""
                      }
                    />
                  </div>
                  <div className="control_group w_50">
                    <label htmlFor="cancellationHours">
                      Cancellation Hours
                    </label>
                    <TextField
                      placeholder="Enter hours"
                      hiddenLabel
                      fullWidth
                      value={formik.values.cancellationHours}
                      id="cancellationHours"
                      name="cancellationHours"
                      className="text_field"
                      inputProps={{ maxLength: 40 }}
                      onChange={(e) => {
                        const { value } = e.target;
                        if (isNumber(value)) {
                          formik.handleChange(e);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      helperText={
                        formik.touched.cancellationHours &&
                        typeof formik.errors.cancellationHours === "string"
                          ? formik.errors.cancellationHours
                          : ""
                      }
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <Box className="custom_tabs2 mt_40 mb_30" sx={{ width: "100%" }}>
            <Tabs
              aria-label="basic tabs example"
              className="custom_tabs_links"
              value={valueTabs}
              onChange={handleChangeTab}
            >
              <Tab {...a11yProps(0)} label="+ Add Custom" />
              <Tab {...a11yProps(1)} label="+ Add Previous Questions" />
            </Tabs>
          </Box>

          <div>
            <div className="control_group w_100 cstm_mn">
              <div className="gap_m">
                <div className="control_group w_50">
                  <label>Select Type</label>

                  <Select
                    className="dropse"
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    inputProps={{
                      "aria-label": "Without label",
                    }}
                    displayEmpty
                    name="inputType"
                    value={value.inputType}
                    onChange={handleChangeType}
                  >
                    <MenuItem value="" disabled>
                      Select Type
                    </MenuItem>
                    <MenuItem value="Checkbox">Checkbox</MenuItem>
                    <MenuItem value="Input">Input</MenuItem>
                    <MenuItem value="TextArea">TextArea</MenuItem>
                    <MenuItem value="Radio">Radio</MenuItem>
                    <MenuItem value="Date">Date</MenuItem>
                  </Select>
                </div>

                <div className="control_group w_50">
                  <label htmlFor="question">Enter Question</label>
                  <TextField
                    id="question"
                    placeholder="Question"
                    hiddenLabel
                    fullWidth
                    value={value.question}
                    onChange={(e) =>
                      setValue((prev) => ({
                        ...prev,
                        question: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            </div>
            {value.inputType === "Checkbox" || value.inputType === "Radio" ? (
              <div className="control_group w_100">
                <label htmlFor="option">Add Options</label>
                <TextField
                  id="option"
                  placeholder="Option Text"
                  hiddenLabel
                  fullWidth
                  value={currentOption}
                  onChange={(e) => setCurrentOption(e.target.value)}
                />
              </div>
            ) : null}

            {value.options.length > 0 && (
              <FormGroup style={{ marginTop: 10 }}>
                {value.options.map((opt, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 4,
                    }}
                  >
                    <span style={{ fontSize: 14, marginRight: 8 }}>
                      {i + 1}.
                    </span>
                    <span>{opt?.optionText}</span>
                  </div>
                ))}
              </FormGroup>
            )}

            <div className="control_group w_100 chkbx_mn">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value.isReused}
                    onChange={(e) =>
                      setValue((prev) => ({
                        ...prev,
                        isReused: e.target.checked,
                      }))
                    }
                  />
                }
                name="remember"
                label="Save this question for other offers"
              />
              {value.inputType === "Checkbox" || value.inputType === "Radio" ? (
                <Button className="btn btn_primary" onClick={handleAddOption}>
                  + Add Option
                </Button>
              ) : null}
            </div>

            <Button
              className="btn btn_primary"
              sx={{ marginBottom: "20px" }}
              onClick={handleSave}
            >
              Save
            </Button>

            {questionsList.length > 0 && (
              <div style={{ marginTop: "10px" }}>
                <h2 style={{ marginBottom: "20px" }}>Saved Questions</h2>
                {questionsList.map((q: any, idx) => (
                  <div
                    key={idx}
                    style={{
                      marginBottom: 10,
                      borderRadius: 8,
                      padding: "8px 12px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <strong style={{ fontSize: "16px" }}>
                          {`${idx + 1}. ${q?.question || q?.label}`}
                        </strong>{" "}
                        ({q?.inputType})
                      </div>
                      <div>
                        <IconButton
                          className="edit_icon"
                          onClick={() => handleEditQuestion(idx)}
                        >
                          <img src="/static/images/edit2_icon.svg" alt="icon" />
                        </IconButton>

                        <IconButton
                          className="edit_icon"
                          onClick={() => handleDeleteQuestion(idx)}
                        >
                          <img
                            src="/static/images/trash2_icon.svg"
                            alt="Delete"
                          />
                        </IconButton>
                      </div>
                    </div>

                    {(q.inputType.toLowerCase() === "checkbox" ||
                      q.inputType.toLowerCase() === "radio") && (
                      <ul className="options_list">
                        {q?.options?.map((opt: any, i: number) => (
                          <li
                            key={i}
                            style={{ marginLeft: 30, marginTop: 5 }}
                            className="option_item"
                          >
                            {opt?.optionText}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <Modal
            className="modal commissionOffer_modal build_offer_modal"
            open={openPrevQModal}
            onClose={() => {
              setValueTabs(0);
              setOpenPrevQModal(false);
            }}
          >
            <div className="modal-dialog" style={{ minWidth: "90%" }}>
              <div className="modal-body">
                <div className="modal_title text_center mb_30">
                  <h2>Add Previous Questions</h2>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 20 }}
                  >
                    <div className="btn_flex flx_end">
                      <Button
                        className="btn btn_primary"
                        onClick={handleSaveQuestion}
                      >
                        Save
                      </Button>
                    </div>

                    <div
                      role="button"
                      tabIndex={0}
                      className="btn-close"
                      onClick={() => {
                        setOpenPrevQModal(false);
                        setValueTabs(0);
                      }}
                    >
                      <CloseIcon />
                    </div>
                  </div>
                </div>
                <div className="control_group w_100">
                  <CustomTabPanel value={valueTabs} index={1}>
                    <div className="cards crds_mn">
                      <Box
                        className="custom_tabs2  mb_30"
                        sx={{ width: "100%" }}
                      >
                        <Tabs
                          aria-label="basic tabs example"
                          className="custom_tabs_links"
                          value={questionTabs}
                          onChange={(e, newValue) => setQuestionTabs(newValue)}
                        >
                          <Tab {...a11yProps(0)} label="Checkbox" />
                          <Tab {...a11yProps(1)} label="Text" />
                          <Tab {...a11yProps(2)} label="Radio" />
                          <Tab {...a11yProps(3)} label="TextArea" />
                          <Tab {...a11yProps(4)} label="Date" />
                        </Tabs>
                      </Box>

                      <CustomTabPanel value={questionTabs} index={0}>
                        <FormGroup className="prvs_qstn_mn">
                          {checkboxQuestions?.length > 0 && (
                            <div className="d_flx1">
                              {checkboxQuestions?.map((q: any, idx) => (
                                <div key={idx} className="question_card">
                                  {/* Checkbox + Title */}
                                  <div className="question_header">
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={selectedQuestions.some(
                                            (item) => item.question === q?.label
                                          )}
                                          onChange={(e) =>
                                            handleCheckboxChange(
                                              q,
                                              e.target.checked
                                            )
                                          }
                                        />
                                      }
                                      label={
                                        <h3 className="question_title">
                                          Question {idx + 1}
                                        </h3>
                                      }
                                    />
                                  </div>

                                  {/* Question Text */}
                                  <p className="question_label">
                                    <strong>{q?.label}</strong>{" "}
                                    <span className="input_type">
                                      ({q.inputType})
                                    </span>
                                  </p>

                                  {/* Options List */}
                                  {q?.options?.length > 0 && (
                                    <ul className="options_list">
                                      {q?.options?.map(
                                        (opt: QuestionOption, i: number) => (
                                          <li key={i} className="option_item">
                                            {opt?.optionText}
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </FormGroup>
                      </CustomTabPanel>

                      <CustomTabPanel value={questionTabs} index={1}>
                        <FormGroup className="prvs_qstn_mn">
                          <div className="d_flx1">
                            {textQuestions.map((q: any, i) => (
                              <div key={i} className="question_card">
                                <div className="question_header">
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={selectedQuestions.some(
                                          (item) => item.question === q?.label
                                        )}
                                        onChange={(e) =>
                                          handleCheckboxChange(
                                            q,
                                            e.target.checked
                                          )
                                        }
                                      />
                                    }
                                    label={<h2>Question {i + 1}</h2>}
                                  />
                                </div>
                                <p>{q?.label}</p>
                              </div>
                            ))}
                          </div>
                        </FormGroup>
                      </CustomTabPanel>

                      <CustomTabPanel value={questionTabs} index={2}>
                        <FormGroup className="prvs_qstn_mn">
                          {radioQuestions?.length > 0 && (
                            <div className="d_flx1">
                              {radioQuestions?.map((q: any, idx) => (
                                <div className="question_card">
                                  <div className="question_header">
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={selectedQuestions?.some(
                                            (item) => item.question === q?.label
                                          )}
                                          onChange={(e) =>
                                            handleCheckboxChange(
                                              q,
                                              e.target.checked
                                            )
                                          }
                                        />
                                      }
                                      label={<h2>Question {idx + 1}</h2>}
                                    />
                                  </div>
                                  <p style={{ fontSize: "16px" }}>{q?.label}</p>{" "}
                                  ({q.inputType})
                                  <ul style={{ marginTop: "10px" }}>
                                    {q?.options?.map(
                                      (opt: QuestionOption, i: number) => (
                                        <li style={{ marginLeft: 20 }} key={i}>
                                          {opt?.optionText}
                                        </li>
                                      )
                                    )}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          )}
                        </FormGroup>
                      </CustomTabPanel>

                      <CustomTabPanel value={questionTabs} index={3}>
                        <FormGroup className="prvs_qstn_mn">
                          <div className="d_flx1">
                            {textAreaQuestions?.map((q: any, i) => (
                              <div key={i} className="question_card">
                                <div className="question_header">
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={selectedQuestions?.some(
                                          (item) => item?.question === q?.label
                                        )}
                                        onChange={(e) =>
                                          handleCheckboxChange(
                                            q,
                                            e.target.checked
                                          )
                                        }
                                      />
                                    }
                                    label={<h2>Question {i + 1}</h2>}
                                  />
                                </div>
                                <p>{q?.label}</p>
                              </div>
                            ))}
                          </div>
                        </FormGroup>
                      </CustomTabPanel>

                      <CustomTabPanel value={questionTabs} index={4}>
                        <FormGroup className="prvs_qstn_mn">
                          <div className="d_flx1">
                            {dateQuestions?.map((q: any, i) => (
                              <div key={i} className="question_card">
                                <div className="question_header">
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={selectedQuestions?.some(
                                          (item) => item?.question === q?.label
                                        )}
                                        onChange={(e) =>
                                          handleCheckboxChange(
                                            q,
                                            e.target.checked
                                          )
                                        }
                                      />
                                    }
                                    label={<h2>Question {i + 1}</h2>}
                                  />
                                </div>
                                <p>{q?.label}</p>
                              </div>
                            ))}
                          </div>
                        </FormGroup>
                      </CustomTabPanel>
                    </div>
                  </CustomTabPanel>
                </div>
              </div>
            </div>
          </Modal>
        </form>
      </div>
    </>
  );
}

export default EventForm;
